import { nowUTC } from "@homebound/activesupport";
import {
  AutoSaveIndicator,
  Button,
  ButtonMenu,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
} from "@homebound/beam";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cycleTrackingAction } from "src/components/CycleTrackingAction";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, SavePlanPackageInput } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PublishAggregateVersionModal } from "src/routes/libraries/plan-package-v2/components/PublishAggregateVersionModal";
import {
  PlanPackageTakeoffPage_TakeoffDetailFragment,
  useSavePlanPackageTakeoffsCycleMutation,
} from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import { fail, pluralize } from "src/utils";

export type TakeoffHeaderProps = {
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  pendingSlotCount: number;
};

export function TakeoffsHeader(props: TakeoffHeaderProps) {
  const { planPackage, pendingSlotCount } = props;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);

  const [saveTakeoffsCycle] = useSavePlanPackageTakeoffsCycleMutation();
  const saveCycle = useCallback(
    (input: Pick<SavePlanPackageInput, "takeoffsStartedAt" | "takeoffsCompletedAt">) =>
      saveTakeoffsCycle({ variables: { input: { id: planPackage.id, ...input } } }),
    [saveTakeoffsCycle, planPackage.id],
  );

  const cycleMenuProps = cycleTrackingAction({
    title: "Plan Cycle",
    startedAt: planPackage.takeoffsStartedAt?.toPlainDate(),
    completedAt: planPackage.takeoffsCompletedAt?.toPlainDate(),
    onStart: () => saveCycle({ takeoffsStartedAt: nowUTC() }),
    onMarkComplete: () => saveCycle({ takeoffsCompletedAt: nowUTC() }),
    onReopen: () => saveCycle({ takeoffsCompletedAt: null }),
    onReset: () => saveCycle({ takeoffsStartedAt: null, takeoffsCompletedAt: null }),
  });

  const { aggregateDraft, name } = planPackage;
  const canPublish = aggregateDraft?.canPublish.allowed;
  const disabledReasons = aggregateDraft?.canPublish.disabledReasons ?? [];
  // Show the Publish button if they can publish, or if they cannot publish but there are no issues - in which case it'll be disabled.
  // Otherwise, we'd show a "View Issues" button that opens an empty modal, which is more confusing than helpful.
  const showPublishButton = canPublish || (!canPublish && disabledReasons.length === 0);

  return (
    <PageHeader
      xss={Css.bgTransparent.bn.mb0.$}
      titleSize="xl2"
      title="Takeoff Manager"
      left={<AutoSaveIndicator />}
      right={
        <>
          {designerModifiedSlotsEnabled && pendingSlotCount > 0 && (
            <Button
              label={`${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`}
              onClick={() =>
                navigate(
                  createPlanPackageIncomingSlotReviewUrl(
                    planPackage.id,
                    planPackage.version?.id ?? fail("Plan package version is required"),
                  ),
                )
              }
              variant="caution"
              icon="todo"
            />
          )}
          <ButtonMenu {...cycleMenuProps} />
          <ReadyPlanVersionHeader readyPlan={planPackage} versionId={planPackage.version?.id} />
          {/* TODO: Work with Product & Design to determine when/how this can be Published */}
          {showPublishButton && (
            <Button
              size="md"
              disabled={!canPublish}
              tooltip={disableBasedOnPotentialOperation(aggregateDraft?.canPublish)}
              label="Publish"
              onClick={() =>
                openModal({
                  content: (
                    <PublishAggregateVersionModal
                      draftAggregateVersion={aggregateDraft ?? fail("No draft aggregate version found")}
                      copyText="This update will be made available to any Design Package and Product Offering that reference it."
                      readyPlanName={name}
                    />
                  ),
                })
              }
            />
          )}
          {!showPublishButton && (
            <Button
              size="md"
              label="View Issues"
              onClick={() => openModal({ content: <DisabledPublishReasons aggregateDraft={aggregateDraft} /> })}
            />
          )}
        </>
      }
    />
  );
}

function DisabledPublishReasons({
  aggregateDraft,
}: {
  aggregateDraft?: PlanPackageTakeoffPage_TakeoffDetailFragment["aggregateDraft"];
}) {
  const { closeModal } = useModal();
  const reasons = aggregateDraft?.canPublish.disabledReasons ?? [];
  return (
    <>
      <ModalHeader>Takeoff Issues</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          {reasons.map((reason) => (
            <pre key={reason.message} css={Css.gray800.m0.add("whiteSpace", "pre-wrap").sm.$}>
              {reason.message}
            </pre>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Close" onClick={() => closeModal()} />
      </ModalFooter>
    </>
  );
}
